// src/pages/Dashboard.jsx
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import SideBar      from './SideBar';
import Header       from './Header';
import StatsCard    from './StatsCard';
import ClientsTable from './ClientsTable';
import EmailSchedulerButton from './EmailSchedulerButton';
import MailboxConnectionBanner from './MailboxConnectionBanner';
import { db } from '../firebase';
import { doc, updateDoc } from 'firebase/firestore';
import '../styles/Dashboard.css';
import { MdSearch, MdClose } from 'react-icons/md';
import { useAuth } from '../contexts/AuthContext';
import { useSessionStorage } from '../contexts/SessionStorageContext';
import { useNotificationsContext } from '../contexts/NotificationsContext';
import {
  DASHBOARD_STATUS_FILTERS,
  clientMatchesStatusFilter,
  countActiveDashboardClients,
  countCompletedDashboardClients,
  sortDashboardClients
} from '../utils/dashboardFilters';

export default function Dashboard() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [lastUpdated, setLastUpdated] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [recentlyCreatedClient, setRecentlyCreatedClient] = useState(false);
  const [autoStartClientId, setAutoStartClientId] = useState(null);
  const [deadLetterReviewTarget, setDeadLetterReviewTarget] = useState(null);
  const { currentUser } = useAuth();
  const { sessionData, updateClientInCache, error: clientsError } = useSessionStorage();

  // Use clients from session storage (context handles fetching)
  const clients = useMemo(() => sessionData.clients || [], [sessionData.clients]);
  const isLoadingClients = sessionData.clients === null;

  // Get notification data from centralized context (no duplicate listeners!)
  const {
    dashboardStats,
    clientNotificationCounts = {},
    clientCurrentActionCounts = {},
    clientPendingSendCounts = {},
    clientCampaignCompletion = {},
    mailboxConnection
  } = useNotificationsContext();

  // Unified loading state for all stats cards (ensures consistent loading animation)
  const statsLoading = sessionData.isLoadingClients || dashboardStats.loading;

  const updateClient = async (id, updates) => {
    const ref = doc(db, 'users', currentUser.uid, 'clients', id);
    await updateDoc(ref, updates);

    // Update cache
    updateClientInCache(id, updates);
  };

  useEffect(() => {
    // whenever clients load or change, update the timestamp
    setLastUpdated(
      new Date().toLocaleString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric',
        hour: '2-digit', minute: '2-digit'
      })
    );
  }, [clients]);

  // Track when a new client is created to trigger dashboard button glow
  const prevClientCountRef = useRef(clients.length);
  useEffect(() => {
    if (clients.length > prevClientCountRef.current && prevClientCountRef.current > 0) {
      // A new client was added (not initial load)
      setRecentlyCreatedClient(true);
      
      // Clear the glow after 5 seconds
      const timer = setTimeout(() => {
        setRecentlyCreatedClient(false);
      }, 5000);
      
      return () => clearTimeout(timer);
    }
    prevClientCountRef.current = clients.length;
  }, [clients.length]);

  // Notifications are now managed by NotificationsContext (no duplicate listeners!)
  // Client loading is also handled by SessionStorageContext

  // Handle URL params for auto-starting client (from AddClientModal redirect)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const clientId = params.get('client');
    const action = params.get('action');

    if (clientId && action === 'start') {
      // Set the client ID to auto-start
      setAutoStartClientId(clientId);
      // Clear the URL params to prevent re-triggering
      navigate('/dashboard', { replace: true });
    }
  }, [location.search, navigate]);

  const filteredClients = useMemo(() => {
    const normalizedSearch = searchTerm.toLowerCase();
    const matchingClients = clients.filter(client => {
      const matchesSearch = (client.name || '').toLowerCase().includes(normalizedSearch);
      const matchesFilter = clientMatchesStatusFilter(client, statusFilter, {
        currentActionCount: clientCurrentActionCounts[client.id] || 0,
        pendingSendCount: clientPendingSendCounts[client.id] || 0,
        totalNotificationCount: clientNotificationCounts[client.id] || 0,
        campaignCompletion: clientCampaignCompletion[client.id]
      });
      return matchesSearch && matchesFilter;
    });
    return sortDashboardClients(matchingClients, { clientCampaignCompletion });
  }, [clients, searchTerm, statusFilter, clientCurrentActionCounts, clientPendingSendCounts, clientNotificationCounts, clientCampaignCompletion]);

  // Both tiles are counted off the same client list the rows are drawn from,
  // so the header can never disagree with what the advisor is looking at.
  const activeClientCount = useMemo(
    () => countActiveDashboardClients(clients),
    [clients]
  );

  const completedClientCount = useMemo(
    () => countCompletedDashboardClients(clients),
    [clients]
  );

  const reviewUnsentDraft = (item) => {
    if (!item?.clientId || !item?.threadId) return;
    setSearchTerm('');
    setStatusFilter('all');
    setDeadLetterReviewTarget({
      clientId: item.clientId,
      notification: {
        id: `dead-letter:${item.id}`,
        kind: 'action_needed',
        priority: 'important',
        clientId: item.clientId,
        threadId: item.threadId,
        email: item.recipient,
        createdAt: item.createdAt,
        meta: {
          reason: 'reply_review_required',
          details: 'Automatic reply was saved for review and was not sent.',
          deadLetterId: item.id,
          replyToMessageId: item.replyToMessageId,
          suggestedEmail: {
            to: item.recipient ? [item.recipient] : [],
            subject: item.subject || '',
            body: item.draftBody
          }
        }
      }
    });
  };

  return (
    <div className="app-layout">
      <SideBar clientCount={clients.length} recentlyCreatedClient={recentlyCreatedClient} isLoadingClients={isLoadingClients} />

      <main className="main-content">
        <MailboxConnectionBanner {...(mailboxConnection || {})} />

        <div className="dashboard-top-row">
          <Header
            stats={[
              {
                label: completedClientCount === 1 ? 'Completed Campaign' : 'Completed Campaigns',
                value: completedClientCount,
                loading: statsLoading
              },
              {
                label: 'Properties Completed',
                value: dashboardStats.propertyMatches,
                loading: statsLoading
              },
              {
                label: 'Sheet Updates',
                value: dashboardStats.sheetUpdates,
                loading: statsLoading
              },
            ]}
          />

          <EmailSchedulerButton
            clients={clients}
            onReviewDraft={reviewUnsentDraft}
          />
        </div>

        <hr className="divider" />
        <section className="clients-overview">
          <div className="clients-overview__header">
            <h2>Your Clients</h2>
            <div className="clients-overview__stats">
              <StatsCard
                label={activeClientCount === 1 ? "Active Client" : "Active Clients"}
                value={activeClientCount}
                loading={statsLoading}
                animationDelay={0}
              />
              <StatsCard
                label={dashboardStats.actionsNeeded === 1 ? "Action Needed" : "Actions Needed"}
                value={dashboardStats.actionsNeeded}
                loading={statsLoading}
                animationDelay={150}
              />
            </div>
          </div>

        {/* Controls Row - the status filter is the only way to the Archived view
            from the dashboard, so it renders even with no campaigns left. */}
        <div className="clients-overview__controls">
          <div className="clients-overview__search">
            <input
              type="text"
              placeholder="Search"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
            {!searchTerm && <MdSearch size={16} className="search-icon" />}
            {searchTerm && (
              <button
                className="search-clear-btn"
                onClick={() => setSearchTerm('')}
                aria-label="Clear search"
              >
                <MdClose size={14} />
              </button>
            )}
          </div>
          <div className="clients-overview__filter">
            <label htmlFor="dashboard-status-filter">Status</label>
            <select
              id="dashboard-status-filter"
              className="status-filter-select"
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
            >
              {DASHBOARD_STATUS_FILTERS.map(filter => (
                <option key={filter.value} value={filter.value}>
                  {filter.label}
                </option>
              ))}
            </select>
          </div>
          <div className="clients-overview__updated">
            Last updated: {lastUpdated}
          </div>
        </div>

        {clientsError && (
          <p role="alert">Campaigns could not be refreshed. Reload to try again.</p>
        )}
          {/* pass only clients matching search and filter */}
        {(!clientsError || clients.length > 0) && <ClientsTable
          clients={filteredClients}
          allClients={clients}
          updateClient={updateClient}
          loading={sessionData.isLoadingClients}
          clientNotificationCounts={clientNotificationCounts}
          clientCampaignCompletion={clientCampaignCompletion}
          autoStartClientId={autoStartClientId}
          onAutoStartHandled={() => setAutoStartClientId(null)}
          externalActionTarget={deadLetterReviewTarget}
          onExternalActionComplete={() => setDeadLetterReviewTarget(null)}
        />}

        </section>
      </main>
    </div>
  );
}
